
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAdCategoryQuery, useGetCustomFieldsQuery, useCreateCustomFieldMutation, useUpdateCustomFieldMutation, useDeleteCustomFieldMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Modal from '../../../components/Modal';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';

interface CustomFieldRow {
  id?: number;
  field_name: string;
  field_name_ar: string;
  field_key?: string;
  field_type: 'text' | 'number' | 'date' | 'dropdown_single' | 'dropdown_multiple' | 'textarea' | 'checkbox' | 'radio' | 'countries' | 'cities' | 'file_single' | 'file_multiple';
  options: Array<{ en: string; ar: string }>;
  is_required: boolean;
  sort_order: number;
  target_user_type: 'all' | 'employer' | 'job_seeker';
}

const AdminAdCategoryCustomFields: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const categoryId = parseInt(id!);
  
  const { data: categoryData, isLoading: isLoadingCategory } = useGetAdCategoryQuery(categoryId);
  const { data: fieldsData, isLoading: isLoadingFields, refetch } = useGetCustomFieldsQuery(categoryId);
  const [createField] = useCreateCustomFieldMutation();
  const [updateField] = useUpdateCustomFieldMutation();
  const [deleteField] = useDeleteCustomFieldMutation();

  const [fields, setFields] = useState<CustomFieldRow[]>([]);
  const [activeTab, setActiveTab] = useState<'employer' | 'job_seeker'>('employer');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomFieldRow | null>(null);

  const category = categoryData?.data;
  const isAgriculturalJob = category?.title?.toLowerCase()?.includes('agricultural job') || category?.parent?.title?.toLowerCase()?.includes('agricultural job') || false;

  useEffect(() => {
    if (fieldsData?.data) {
      const fieldsList = fieldsData.data.map((field: any) => ({
        id: field.id,
        field_name: field.field_name || '',
        field_name_ar: field.field_name_ar || '',
        field_key: field.field_key || '',
        field_type: field.field_type || 'text',
        options: field.options || [],
        is_required: field.is_required || false,
        sort_order: field.sort_order || 0,
        target_user_type: field.target_user_type || 'all',
      }));
      setFields(fieldsList);
    } else {
      setFields([]);
    }
  }, [fieldsData]);

  const shouldShow = (field: CustomFieldRow) => {
    if (!isAgriculturalJob) return true;
    return field.target_user_type === activeTab;
  };

  const visibleFieldsCount = fields.filter(shouldShow).length;

  const openAddModal = () => {
    setEditingField({
      field_name: '',
      field_name_ar: '',
      field_type: 'text',
      options: [],
      is_required: false,
      sort_order: fields.length,
      target_user_type: isAgriculturalJob ? activeTab : 'all',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (field: CustomFieldRow) => {
    setEditingField(JSON.parse(JSON.stringify(field))); // Deep copy
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingField(null);
    setIsModalOpen(false);
  };

  const handleModalFieldChange = (field: keyof CustomFieldRow, value: any) => {
    if (!editingField) return;
    setEditingField(prev => ({ ...prev!, [field]: value }));
  };

  const handleModalOptionChange = (optionIndex: number, lang: 'en' | 'ar', value: string) => {
    if (!editingField) return;
    const options = [...editingField.options];
    options[optionIndex] = { ...options[optionIndex], [lang]: value };
    setEditingField(prev => ({ ...prev!, options }));
  };

  const addModalOption = () => {
    if (!editingField) return;
    setEditingField(prev => ({
      ...prev!,
      options: [...prev!.options, { en: '', ar: '' }]
    }));
  };

  const removeModalOption = (optionIndex: number) => {
    if (!editingField) return;
    setEditingField(prev => ({
      ...prev!,
      options: prev!.options.filter((_, i) => i !== optionIndex)
    }));
  };

  const handleSaveField = async () => {
    if (!editingField) return;
    
    if (!editingField.field_name || !editingField.field_name_ar) {
      showError('Field name (English and Arabic) is required!');
      return;
    }

    if ((editingField.field_type === 'dropdown_single' || editingField.field_type === 'dropdown_multiple' || editingField.field_type === 'radio') && editingField.options.length === 0) {
      showError('Please add at least one option for dropdown/radio fields!');
      return;
    }

    if ((editingField.field_type === 'dropdown_single' || editingField.field_type === 'dropdown_multiple' || editingField.field_type === 'radio')) {
      const hasEmptyOptions = editingField.options.some(opt => !opt.en || !opt.ar);
      if (hasEmptyOptions) {
        showError('All options must have both English and Arabic values!');
        return;
      }
    }

    try {
      const fieldData = {
        category_id: categoryId,
        field_name: editingField.field_name,
        field_name_ar: editingField.field_name_ar,
        field_type: editingField.field_type,
        options: (editingField.field_type === 'dropdown_single' || editingField.field_type === 'dropdown_multiple' || editingField.field_type === 'radio') ? editingField.options : null,
        is_required: editingField.is_required,
        sort_order: editingField.sort_order,
        target_user_type: editingField.target_user_type,
      };

      if (editingField.id) {
        // Update existing field
        await updateField({ id: editingField.id, data: fieldData }).unwrap();
        showSuccess('Custom field updated successfully!');
      } else {
        // Create new field
        await createField(fieldData).unwrap();
        showSuccess('Custom field created successfully!');
      }
      closeModal();
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to save custom field. Please try again.');
    }
  };

  const handleDeleteField = async (field: CustomFieldRow) => {
    if (!field.id) return;

    const confirmed = await confirmAction(
      'Delete Custom Field',
      'Are you sure you want to delete this custom field?'
    );

    if (!confirmed) return;

    try {
      await deleteField(field.id).unwrap();
      showSuccess('Custom field deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete custom field. Please try again.');
    }
  };

  if (isLoadingCategory || isLoadingFields) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                Custom Fields
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-categories'); }} className="text-muted text-hover-primary">
                    Ad Categories
                  </a>
                </li>
                {category?.parent_id && (
                  <>
                    <li className="breadcrumb-item">
                      <span className="bullet bg-gray-400 w-5px h-2px"></span>
                    </li>
                    <li className="breadcrumb-item text-muted">
                      <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/admin/ad-categories?parent_id=${category.parent_id}`); }} className="text-muted text-hover-primary">
                        {category.parent?.title || 'Parent Category'}
                      </a>
                    </li>
                  </>
                )}
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  {category?.title || 'Category'} - Custom Fields
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => {
                  if (category?.parent_id) {
                    navigate(`/admin/ad-categories?parent_id=${category.parent_id}`);
                  } else {
                    navigate('/admin/ad-categories');
                  }
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={openAddModal}
              >
                Add Custom Field
              </button>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold fs-3 mb-1">Custom Fields for: {category?.title || 'Category'}</span>
                    <span className="text-muted mt-1 fw-semibold fs-7">Manage custom fields for this ad category</span>
                  </h3>
                </div>
                {isAgriculturalJob && (
                  <div className="card-toolbar">
                    <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
                      <li className="nav-item">
                        <a 
                          className={`nav-link ${activeTab === 'employer' ? 'active' : ''} cursor-pointer`}
                          onClick={() => setActiveTab('employer')}
                        >
                          For Employer
                        </a>
                      </li>
                      <li className="nav-item">
                        <a 
                          className={`nav-link ${activeTab === 'job_seeker' ? 'active' : ''} cursor-pointer`}
                          onClick={() => setActiveTab('job_seeker')}
                        >
                          For Job Seeker
                        </a>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <div className="card-body pt-0">
                {visibleFieldsCount === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-muted">No custom fields found. Click "Add Custom Field" to create one.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                      <thead>
                        <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                          <th className="min-w-100px">Field Name (EN)</th>
                          <th className="min-w-100px">Field Name (AR)</th>
                          <th className="min-w-100px">Field Key</th>
                          <th className="min-w-100px">Type</th>
                          <th className="min-w-100px">Options</th>
                          <th className="min-w-50px">Required</th>
                          <th className="min-w-50px">Sort Order</th>
                          <th className="text-end min-w-100px">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600 fw-semibold">
                        {fields.map((field, index) => {
                          if (!shouldShow(field)) return null;
                          return (
                          <tr key={field.id || index}>
                            <td>{field.field_name}</td>
                            <td>{field.field_name_ar}</td>
                            <td>
                              <span className="badge badge-light-info">{field.field_key}</span>
                            </td>
                            <td>
                              <span className="badge badge-light-primary">{field.field_type}</span>
                            </td>
                            <td>
                              {(field.field_type === 'dropdown_single' || field.field_type === 'dropdown_multiple' || field.field_type === 'radio') ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {field.options.map((opt, i) => (
                                    <span key={i} className="badge badge-light-secondary text-gray-700">
                                      {opt.en}
                                    </span>
                                  ))}
                                </div>
                              ) : '-'}
                            </td>
                            <td>
                              {field.is_required ? (
                                <span className="badge badge-light-success">Yes</span>
                              ) : (
                                <span className="badge badge-light-secondary">No</span>
                              )}
                            </td>
                            <td>{field.sort_order}</td>
                            <td className="text-end">
                              <div className="d-flex justify-content-end gap-2">
                                <button disabled
                                  className="btn btn-icon btn-sm btn-light-primary"
                                  onClick={() => openEditModal(field)}
                                >
                                  <i className="fas fa-pencil-alt fs-6"></i>
                                </button>
                                <button disabled
                                  className="btn btn-icon btn-sm btn-light-danger"
                                  onClick={() => handleDeleteField(field)}
                                >
                                  <i className="fas fa-trash fs-6"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingField?.id ? 'Edit Custom Field' : 'Add Custom Field'}
        size="lg"
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSaveField}>Save Changes</button>
          </>
        }
      >
        {editingField && (
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label required">Field Name (EN)</label>
              <input
                type="text"
                className="form-control"
                value={editingField.field_name}
                onChange={(e) => handleModalFieldChange('field_name', e.target.value)}
                placeholder="Enter field name in English"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label required">Field Name (AR)</label>
              <input
                type="text"
                className="form-control"
                value={editingField.field_name_ar}
                onChange={(e) => handleModalFieldChange('field_name_ar', e.target.value)}
                placeholder="Enter field name in Arabic"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label required">Field Type</label>
              <select
                className="form-select"
                value={editingField.field_type}
                onChange={(e) => handleModalFieldChange('field_type', e.target.value)}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="textarea">Textarea</option>
                <option value="checkbox">Checkbox</option>
                <option value="radio">Radio</option>
                <option value="dropdown_single">Dropdown (Single)</option>
                <option value="dropdown_multiple">Dropdown (Multiple)</option>
                <option value="countries">Country list</option>
                <option value="cities">City list</option>
                <option value="file_single">File (Single)</option>
                <option value="file_multiple">File (Multiple)</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Sort Order</label>
              <input
                type="number"
                className="form-control"
                value={editingField.sort_order}
                onChange={(e) => handleModalFieldChange('sort_order', parseInt(e.target.value) || 0)}
              />
            </div>
            
            <div className="col-md-12">
              <div className="form-check form-check-custom form-check-solid">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={editingField.is_required}
                  onChange={(e) => handleModalFieldChange('is_required', e.target.checked)}
                  id="isRequiredCheck"
                />
                <label className="form-check-label" htmlFor="isRequiredCheck">
                  Is Required?
                </label>
              </div>
            </div>

            {(editingField.field_type === 'dropdown_single' || editingField.field_type === 'dropdown_multiple' || editingField.field_type === 'radio') && (
              <div className="col-12 mt-4">
                <label className="form-label">Options</label>
                <div className="table-responsive border rounded p-3">
                  <table className="table table-row-dashed table-row-gray-300 gy-4 align-middle">
                    <thead>
                      <tr className="fw-bold fs-6 text-gray-800">
                        <th>English Label</th>
                        <th>Arabic Label</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingField.options.map((opt, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={opt.en}
                              onChange={(e) => handleModalOptionChange(index, 'en', e.target.value)}
                              placeholder="Option (EN)"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={opt.ar}
                              onChange={(e) => handleModalOptionChange(index, 'ar', e.target.value)}
                              placeholder="Option (AR)"
                            />
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-light-danger"
                              onClick={() => removeModalOption(index)}
                            >
                              <i className="fas fa-trash fs-6"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary mt-2"
                    onClick={addModalOption}
                  >
                    + Add Option
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminAdCategoryCustomFields;
